<template>
  <div class="performance-item-container">
    <div class="search-box">
      <div v-if="showTitle" class="title">{{ $t('title.performanceItem') }}</div>
      <lego-text-field
        class="input-text"
        v-model="searchMetricName"
        counter="100"
        sm
        searchable
        disableInputMutation
        :placeholder="$t('searchLabel.searchLike', { VPC: $t('title.performanceItem') })"
        :isCounter="false"
        :isFreeText="true"
      />
    </div>
    <div class="scroll-box">
      <div v-if="isShowMessageNoProductType" class="noDataText">
        <div>
          <img src="@/assets/img/scmp/no_data.png" />
          <div>{{ $t('message.pleaseSelectProductType') }}</div>
        </div>
      </div>
      <template v-else>
        <div v-if="!dataSearch.length" class="no-result-found">
          {{ $t('message.noResultFound') }}
        </div>
        <c-scroll v-else @scrolling="onScrolling">
          <lego-accordion-item
            v-for="(item, index) in dataSearch"
            :key="index"
            :index="index"
            expandable
            class="accordion-item"
          >
            <template #title>
              <div class="accordion-title">
                <span>
                  {{ item.metricName }}
                  <em v-if="item.children">({{ item.children.length }})</em>
                </span>
              </div>
            </template>
            <template #expand>
              <div
                v-for="(child, idx) in item.children"
                :key="idx"
                class="accordion-sub-item-wrap"
                @mouseover="tooltipPerformanceData(item, idx)"
                v-lego-tooltip="{
                  value: tooltipData,
                  position: 'top',
                  timer: 1000,
                  isShowHTML: true
                }"
              >
                <div
                  class="accordion-sub-item"
                  :class="isMetricSelected(child.metricKey) ? 'checked' : ''"
                  @click.stop="handleClickPerformanceItem(child)"
                >
                  <lego-checkbox
                    v-if="type == 'checkbox'"
                    :checked="isMetricSelected(child.metricKey)"
                    class="accordion-icon"
                  />

                  <lego-radio
                    v-if="type == 'radio'"
                    class="accordion-icon"
                    :checked="isMetricSelected(child.metricKey)"
                  />
                  <c-label
                    :class="isMetricSelected(child.metricKey) ? 'checked' : ''"
                    :ellipsis="false"
                    style="max-width: 90%"
                  >
                    <div style="display: flex; justify-content: space-between">
                      <span class="item-name">
                        {{ child.metricName }}
                      </span>
                      <span v-if="counter[child.metricKey]" class="counter">
                        {{ counter[child.metricKey] }}
                      </span>
                    </div>
                  </c-label>
                  <lego-icon
                    v-if="type == 'multiple'"
                    icon="add_multiple_performance_item"
                    class="icon-add-multiple"
                  />
                </div>
                <TooltipPerformanceItem
                  :ref="`tooltipPerformanceItem${item.name || item.metricName}`"
                  v-show="false"
                  :dataTooltip="{ metric: child }"
                />
              </div>
            </template>
          </lego-accordion-item>
        </c-scroll>
      </template>
    </div>
  </div>
</template>
<script>
import TooltipPerformanceItem from '@/containers/components/TooltipPerformanceItem.vue';
import legoEventBus from '@/lego-component/utils/eventBus/eventBus';

export default {
  name: 'performance-item',
  emits: ['close_tooltips', 'on-selected-metric'],
  components: { TooltipPerformanceItem },
  props: {
    metricList: {
      type: Array,
      default: () => [],
      required: true
    },
    selectedMetrics: {
      type: Array,
      default: () => [],
      required: true
    },
    showTitle: {
      type: Boolean,
      default: () => false
    },
    type: {
      type: String,
      default: 'checkbox' // checkbox | radio | multiple
    },
    page: {
      type: String,
      default: ''
    }
  },
  inject: ['isShowMessageNoProductType'],
  computed: {
    tooltipWidth() {
      return this.type === 'radio' ? '300px' : this.page === 'eventTemplate' ? '350px' : '330px';
    },
    dataSearch() {
      if (!this.searchMetricName) return this.metricList;

      const searchKey = this.searchMetricName.toLowerCase();

      return this.metricList
        .map((item) => {
          const newItem = this.$util.deepCopy(item);
          newItem.children = item.children.filter((el) =>
            el.metricName.toLowerCase().includes(searchKey)
          );
          return newItem;
        })
        .filter((item) => item.children.length);
    }
  },
  watch: {
    selectedMetrics: {
      handler(v) {
        this.counter = {};
        if (this.type !== 'multiple') return;

        v.forEach(({ metricKey }) => {
          if (this.counter[metricKey] == undefined) {
            this.counter[metricKey] = 1;
          } else {
            this.counter[metricKey]++;
          }
        });
      },
      deep: true
    },
    metricList: {
      handler() {
        this.searchMetricName = '';
      },
      deep: true,
      immediate: true
    }
  },
  data() {
    return {
      searchMetricName: '',
      counter: {},
      tooltipData: ''
    };
  },
  methods: {
    tooltipPerformanceData(item, index) {
      this.tooltipData = '';
      if (this.$refs[`tooltipPerformanceItem${item.name || item.metricName}`])
        this.tooltipData =
          this.$refs[`tooltipPerformanceItem${item.name || item.metricName}`][index].$el.innerHTML;
    },
    onScrolling() {
      legoEventBus.$emit('close_tooltips');
    },
    isMetricSelected(itemKey) {
      return !!this.selectedMetrics.find((d) => d.metricKey == itemKey);
    },

    handleClickPerformanceItem(performanceItem) {
      legoEventBus.$emit('close_tooltips');
      this.$emit('on-selected-metric', performanceItem);
    }
  }
};
</script>

<style lang="scss" scoped>
$input-height: 32px !important;
.performance-item-container {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;

  .search-box {
    padding-right: 24px;

    .title {
      color: #2f3237;
      font-family: $lego__font-family;
      font-size: 16px;
      font-style: normal;
      font-weight: 500;
      margin-bottom: 16px;
      line-height: 16px;
    }

    :deep .lego-text-field__input {
      height: $input-height;
      font-size: 14px;
      border: 1px solid var(--color-ui-14);
      &:hover {
        border-color: #2c59d9;
      }
    }

    :deep .lego-text-field__input::placeholder {
      color: #b5b9bf;
      font-family: $lego__font-family;
      font-size: 14px;
      font-style: normal;
      line-height: 16px;
    }

    :deep .lego-text-field--small .lego-text-field__icon-wrap {
      height: $input-height;
    }
  }

  .scroll-box {
    height: 100%;
    max-height: 100%;
    overflow: auto;
    user-select: none;
    .no-result-found {
      margin-top: 16px;
      padding-left: 8px;
      color: #878e9b;
      font-family: $lego__font-family;
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 16px;
      overflow: hidden;
    }

    .accordion-item {
      border: none;
      margin-right: 24px;

      .accordion-title {
        display: flex;
        justify-content: space-between;

        span {
          font-size: 15px;
          font-style: normal;
          line-height: 16px;
          color: #2f3237;
          font-weight: 500;
        }

        em {
          font-style: normal;
        }
      }

      :deep .accordion-list-item__main {
        height: 48px !important;
        border: none;
        border-radius: 4px;
        padding: 0px 0px 0px 16px;

        &:hover {
          background-color: var(--color-ui-15);
        }

        &:hover {
          .lego-icon {
            color: var(--color-ui-13);
          }
        }
      }

      :deep .accordion-list-item__expand {
        border: none;
        padding: 0px 0px 0px 16px;
        margin: 0;

        .accordion-sub-item-wrap {
          .accordion-sub-item {
            display: flex;
            align-items: center;
            height: 40px;
            border: none;
            border-radius: 4px;
            padding: 0px 0px 0px 16px;

            &:hover {
              background-color: var(--color-ui-15);
              cursor: pointer;
            }

            &.checked {
              background-color: var(--color-ui-16);
            }

            .accordion-icon {
              width: 16px;
              height: 16px;
              margin-right: 8px;
              pointer-events: none;
            }

            .c-label {
              color: #585c65 !important;
              font-family: $lego__font-family;
              font-size: 15px !important;
              font-style: normal !important;
              font-weight: normal !important;

              &.checked {
                color: var(--color-ui-17) !important;
              }
            }

            .icon-add-multiple {
              font-size: 20px;
              padding-right: 6px;
            }

            .item-name {
              max-width: 90%;
              text-overflow: ellipsis;
              overflow: hidden;
              white-space: nowrap;
            }

            .counter {
              font-weight: bold;
              color: #374fc8;
              border-radius: 100px;
              min-width: 20px;
              display: flex;
              justify-content: center;
              font-size: 12px;
              align-items: center;
              height: 20px;
              padding-bottom: 2px;
              margin-right: 8px;
            }
          }
        }

        .accordion-sub-item-wrap + .accordion-sub-item-wrap {
          margin-top: 2px;
        }
      }
    }

    .accordion-item + .accordion-item {
      margin-top: 0px;
    }
  }
}
.noDataText {
  height: 100%;
  display: flex;
  align-items: center;
  div {
    text-align: center;
    width: 100%;
    font-size: 14px;
    color: #8a8f99;
    img {
      margin-bottom: 12px;
    }
  }
}
</style>
